import React from "react";
import {
  Addon,
  CBCustomer,
  EntityType,
  Subscription,
} from "../../../../api/grpc/subscription/subscription";
import { Currencies } from "../../../../lib/helpers/getCurrencySymbol";
import { hasUnlimitedUsersAddon } from "../../../../lib/helpers/hasUnlimitedUsersAddon";
import { totalBill } from "../../../../lib/helpers/totalBill";
import { getAddonsName } from "../../lib/getAddonsName";
import {
  getSubscriptionAddons,
  getSubscriptionItems,
} from "../../lib/getSubscriptionItems";
import { Icon, IconDefinition } from "../../../shared/Icon/Icon";
import { NoAddons } from "./NoAddons";

import "./SubscriptionRow.scss";

export interface DetailsData {
  title: string;
  content: string | JSX.Element;
  className?: string;
}

interface Props {
  subscription?: Subscription;
  partnerData?: CBCustomer;
  /** Rendered next to the price, e.g. the "Edit plan" button. */
  children?: React.ReactNode;
}

const ITEM_ICONS: Partial<Record<EntityType, IconDefinition>> = {
  [EntityType.ROOM]: "room_icon_18x18",
  [EntityType.USER]: "user-simple",
  [EntityType.DESK]: "desk-rounded",
  [EntityType.WAYFINDER]: "wayFinder_icon_18x18",
};

const getItemName = (item: Addon) =>
  getAddonsName(item.name.toLocaleLowerCase(), item.quantity, item.entityType);

const withDate = (label: string, date?: string) =>
  date ? `${label} · ${date}` : label;

export const SubscriptionItems = ({
  subscription,
  partnerData,
  children,
}: Props) => {
  const subscriptionAddons = subscription?.addons || [];
  const subscriptionItems = getSubscriptionItems(subscriptionAddons);
  const hasUnlimitedUsers = hasUnlimitedUsersAddon(subscriptionAddons);
  const addons = getSubscriptionAddons(subscriptionAddons);
  const billing = subscription?.billingStatus;
  const isPartnerManaged = !!partnerData?.email;
  const isTrial = subscription?.status?.toLowerCase() === "in_trial";

  const billingStatus = isPartnerManaged
    ? "Managed by Channel Partner"
    : isTrial
      ? withDate("Free trial expires", billing?.upcomingBillingAt)
      : withDate("Upcoming charges", billing?.upcomingBillingAt);

  const sections: DetailsData[] = [
    {
      title: "Subscription items",
      content:
        subscriptionItems.length > 0 ? (
          <ul className="SubscriptionRow__items">
            {subscriptionItems.map((item) => (
              <li
                key={`${item.entityType}-${item.id}`}
                className="SubscriptionRow__item"
              >
                <span
                  className="SubscriptionRow__itemIcon flex-center"
                  aria-hidden="true"
                >
                  <Icon icon={ITEM_ICONS[item.entityType]} />
                </span>
                <span className="SubscriptionRow__itemText">
                  <span>{getItemName(item)}</span>
                  <strong>
                    {hasUnlimitedUsers && item.entityType === EntityType.USER
                      ? "Unlimited"
                      : `${item.quantity}×`}
                  </strong>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="SubscriptionRow__text">-</p>
        ),
    },
    {
      title: "Add-ons",
      content:
        addons.length > 0 ? (
          <ul className="SubscriptionRow__addons">
            {addons.map((item) => (
              <li key={`${item.entityType}-${item.id}`}>{getItemName(item)}</li>
            ))}
          </ul>
        ) : (
          <NoAddons />
        ),
    },
    {
      title: "Billing status",
      content: <p className="SubscriptionRow__text">{billingStatus}</p>,
    },
  ];

  return (
    <div className="SubscriptionRow SubscriptionRow--items">
      {sections.map((section) => (
        <div key={section.title}>
          <h3 className="SubscriptionRow__heading">{section.title}</h3>
          {section.content}
        </div>
      ))}

      <div className="SubscriptionRow__footer main-flex gap-1">
        {!isPartnerManaged && !isTrial && (
          <p className="SubscriptionRow__price">
            <strong>
              {totalBill(billing?.currency as Currencies, billing?.price || 0)}
            </strong>
            <span>
              {subscription?.name.includes("1-Year") ? "/ year" : "/ 3 years"}
            </span>
          </p>
        )}
        {children}
      </div>
    </div>
  );
};
